/**
 * Export XLSX/PDF de un solo cierre diario.
 * Totales = cierreDiarioCards (mismas 8 magnitudes que CierreDiarioListado).
 * Run tests: npx tsx --test src/modules/CajaBancos/cierreDiarioExport.test.ts
 */

import { jsPDF } from "jspdf";
import type { CierreDiario, CierreDiarioDetalle } from "@/lib/api";
import { formatDateOnlyEsAR } from "@/lib/utils";
import {
  chequesNeto,
  cierreDolaresCardView,
  efectivoNeto,
  electronico,
  readCierreCardAmounts,
  detalleSinImporte,
  sortCierreDetalles,
  totalBrutoCaja,
  totalNetoCaja,
} from "./cierreDiarioCards";
import { cierreDetalleDestinatario } from "./cierreDestinatario";
import { detalleEsUsd, usdDhDisplayAmount } from "./cierreDetalleUsd";

type JsPdfDoc = InstanceType<typeof jsPDF>;

export const CIERRE_EXPORT_XLSX_DETALLE_HEADERS = [
  "Concepto",
  "Referencia",
  "Tipo",
  "Método",
  "Importe",
  "Usuario",
  "Destinatario",
] as const;

export const CIERRE_EXPORT_PDF_DETALLE_HEADERS = [
  "Concepto",
  "Referencia",
  "Tipo",
  "Método Pago",
  "Debe (ARS)",
  "D/H (USD)",
  "Haber (ARS)",
  "Usuario",
  "Destinatario",
] as const;

const CARD_NOTES: Record<string, string> = {
  "Saldo Inicial": "Arrastre ARS",
  Efectivo: "Ingresos − egresos del medio",
  Cheques: "Ingresos − egresos del medio",
  Electrónico: "Tarjetas + transferencias (neto)",
  "Cta Cte": "Total facturas; no entra a neto/bruto",
  Dólares: "Saldo inicial USD + D − H; sin leyenda duplicada",
  "Total Neto": "SI + efectivo",
  "Total Bruto": "Neto + cheques + electrónico",
};

/** Fills de cards alineados a CierreDiarioListado (grises / azul / violeta / cian / ámbar / esmeralda). */
const CARD_FILLS: Record<string, string> = {
  "Saldo Inicial": "FFF3F4F6",
  Efectivo: "FFDBEAFE",
  Cheques: "FFF3E8FF",
  Electrónico: "FFCFFAFE",
  "Cta Cte": "FFFEF3C7",
  Dólares: "FFD1FAE5",
  "Total Neto": "FFDCFCE7",
  "Total Bruto": "FFBBF7D0",
};

const XL = {
  text: "FF111827",
  muted: "FF6B7280",
  titleBg: "FFF3F4F6",
  thead: "FFDCE8F2",
  zebra: "FFF9FAFB",
  line: "FFD1D5DB",
  abierto: "FFFEF3C7",
  cerrado: "FFE5E7EB",
  totalAmt: "FF047857",
} as const;

const XL_ARS = '"$"#,##0.00';
const XL_USD = '"USD" #,##0.00';
const THIN = { style: "thin" as const, color: { argb: XL.line } };
const THIN_BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

export type CierreExportCard = {
  label: string;
  value: string;
  amount: number;
  currency: "ARS" | "USD";
  nota: string;
  emphasize: boolean;
};

export type CierreExportDetalleRow = {
  concepto: string;
  referencia: string;
  tipo: "Ingreso" | "Gasto";
  metodo: string;
  importe: string;
  importeNum: number | null;
  importeCurrency: "ARS" | "USD" | null;
  debe: string;
  haber: string;
  usdDh: string;
  usuario: string;
  destinatario: string;
};

export type CierreExportModel = {
  id: number;
  fechaIso: string;
  fechaLabel: string;
  sucursal: string;
  estado: "Abierto" | "Cerrado";
  archivo: string;
  cards: CierreExportCard[];
  detalle: CierreExportDetalleRow[];
};

export type CierrePdfPageSpec = {
  orientation: "landscape" | "portrait";
  format: "a4" | "a3" | [number, number];
  widthMm: number;
  heightMm: number;
  contentHeightMm: number;
};

const A4_LANDSCAPE = { w: 297, h: 210 };
const A3_LANDSCAPE = { w: 420, h: 297 };
const MARGIN_MM = 12;
const HEADER_MM = 22;
const CARDS_MM = 44;
const TABLE_HEAD_MM = 8;
const ROW_MM = 6.2;
const FOOTER_MM = 10;

function dash(value: string | null | undefined): string {
  const s = String(value ?? "").trim();
  return s || "—";
}

function formatArs(n: number): string {
  return `$${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatUsd(n: number): string {
  return `USD ${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Cede el event loop para que la barra de módulos no se congele durante exceljs/jsPDF. */
export function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

function slugSucursal(nombre: string): string {
  const s = nombre
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "sucursal";
}

function sucursalNombre(cierre: CierreDiario): string {
  return String(cierre.sucursalNombre || cierre.sucursal_nombre || "").trim() || "Sucursal";
}

function usuarioNombre(mov: CierreDiarioDetalle): string {
  return dash(mov.usuarioNombre || mov.usuario_nombre);
}

function metodoLabel(mov: CierreDiarioDetalle): string {
  const raw = String(mov.metodoPago ?? mov.metodo_pago ?? "").trim();
  return raw ? raw.replace(/_/g, " ") : "—";
}

function fechaIsoOf(cierre: CierreDiario): string {
  const raw = String(cierre.fecha || "");
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : raw.slice(0, 10);
}

export function cierreExportFilename(model: Pick<CierreExportModel, "id" | "fechaIso" | "sucursal">, ext: "xlsx" | "pdf"): string {
  return `cierre_${model.id}_${model.fechaIso}_${slugSucursal(model.sucursal)}.${ext}`;
}

/** Misma jerarquía de cards que el expandido de CierreDiarioListado. */
export function buildCierreExportModel(cierre: CierreDiario): CierreExportModel {
  const amounts = readCierreCardAmounts(cierre as unknown as Record<string, unknown>);
  const usd = cierreDolaresCardView(amounts);
  const neto = totalNetoCaja(amounts);
  const bruto = totalBrutoCaja(amounts);
  const id = Number(cierre.id) || 0;
  const fechaIso = fechaIsoOf(cierre);
  const sucursal = sucursalNombre(cierre);
  const cards: CierreExportCard[] = [
    {
      label: "Saldo Inicial",
      value: formatArs(amounts.saldoInicial),
      amount: amounts.saldoInicial,
      currency: "ARS",
      nota: CARD_NOTES["Saldo Inicial"],
      emphasize: false,
    },
    {
      label: "Efectivo",
      value: formatArs(efectivoNeto(amounts)),
      amount: efectivoNeto(amounts),
      currency: "ARS",
      nota: CARD_NOTES.Efectivo,
      emphasize: false,
    },
    {
      label: "Cheques",
      value: formatArs(chequesNeto(amounts)),
      amount: chequesNeto(amounts),
      currency: "ARS",
      nota: CARD_NOTES.Cheques,
      emphasize: false,
    },
    {
      label: "Electrónico",
      value: formatArs(electronico(amounts)),
      amount: electronico(amounts),
      currency: "ARS",
      nota: CARD_NOTES.Electrónico,
      emphasize: false,
    },
    {
      label: "Cta Cte",
      value: formatArs(amounts.totalFacturas),
      amount: amounts.totalFacturas,
      currency: "ARS",
      nota: CARD_NOTES["Cta Cte"],
      emphasize: false,
    },
    {
      label: "Dólares",
      value: formatUsd(usd.amount),
      amount: usd.amount,
      currency: "USD",
      nota: CARD_NOTES.Dólares,
      emphasize: false,
    },
    {
      label: "Total Neto",
      value: formatArs(neto),
      amount: neto,
      currency: "ARS",
      nota: CARD_NOTES["Total Neto"],
      emphasize: true,
    },
    {
      label: "Total Bruto",
      value: formatArs(bruto),
      amount: bruto,
      currency: "ARS",
      nota: CARD_NOTES["Total Bruto"],
      emphasize: true,
    },
  ];

  const detalle = sortCierreDetalles(cierre.detalles || [], "default", "asc").map((mov) => {
    const esIngreso = Boolean(mov.esIngreso ?? mov.es_ingreso);
    const sinImporte = detalleSinImporte(mov);
    const esUsd = detalleEsUsd(mov);
    const usdDhAmt = usdDhDisplayAmount(mov);
    const debe =
      !sinImporte && esIngreso && !esUsd
        ? formatArs(Number(mov.importeArs ?? mov.importe_ars ?? mov.importe) || 0)
        : "—";
    const haber =
      !sinImporte && !esIngreso && !esUsd ? formatArs(Number(mov.importe) || 0) : "—";
    const usdDh = !sinImporte && usdDhAmt > 0 ? formatUsd(usdDhAmt) : "—";
    const dest = cierreDetalleDestinatario(mov);
    const importe = sinImporte ? "—" : esUsd ? usdDh : esIngreso ? debe : haber;
    const importeNum = sinImporte
      ? null
      : esUsd
      ? usdDhAmt > 0
        ? usdDhAmt
        : null
      : esIngreso
        ? Number(mov.importeArs ?? mov.importe_ars ?? mov.importe) || 0
        : Number(mov.importe) || 0;
    const importeCurrency: "ARS" | "USD" | null = sinImporte
      ? null
      : esUsd
      ? usdDhAmt > 0
        ? "USD"
        : null
      : "ARS";
    return {
      concepto: dash(mov.descripcion),
      referencia: dash(mov.referencia),
      tipo: sinImporte && !esIngreso ? "—" : esIngreso ? "Ingreso" : "Gasto",
      metodo: metodoLabel(mov),
      importe,
      importeNum,
      importeCurrency,
      debe,
      haber,
      usdDh,
      usuario: usuarioNombre(mov),
      destinatario: dest.show ? dest.nombre : "—",
    };
  });

  return {
    id,
    fechaIso,
    fechaLabel: formatDateOnlyEsAR(fechaIso),
    sucursal,
    estado: cierre.cerrado ? "Cerrado" : "Abierto",
    archivo: `cierre_${id}_${fechaIso}_${slugSucursal(sucursal)}`,
    cards,
    detalle,
  };
}

export function estimateCierrePdfContentHeightMm(rowCount: number): number {
  return (
    MARGIN_MM * 2 +
    HEADER_MM +
    CARDS_MM +
    TABLE_HEAD_MM +
    Math.max(rowCount, 1) * ROW_MM +
    FOOTER_MM
  );
}

/**
 * Página para que el cierre entre completo: landscape A4/A3, o alto custom.
 * Nunca recorta el último renglón.
 */
export function chooseCierrePdfPage(rowCount: number): CierrePdfPageSpec {
  const contentHeightMm = estimateCierrePdfContentHeightMm(rowCount);
  const pad = 6;
  const neededH = contentHeightMm + pad;

  if (rowCount <= 8 && neededH <= A4_LANDSCAPE.h) {
    return {
      orientation: "landscape",
      format: "a4",
      widthMm: A4_LANDSCAPE.w,
      heightMm: A4_LANDSCAPE.h,
      contentHeightMm,
    };
  }
  if (neededH <= A3_LANDSCAPE.h) {
    return {
      orientation: "landscape",
      format: "a3",
      widthMm: A3_LANDSCAPE.w,
      heightMm: A3_LANDSCAPE.h,
      contentHeightMm,
    };
  }
  const widthMm = A3_LANDSCAPE.w;
  const heightMm = Math.ceil(neededH);
  return {
    orientation: heightMm > widthMm ? "portrait" : "landscape",
    format: [widthMm, heightMm],
    widthMm,
    heightMm,
    contentHeightMm,
  };
}

function pdfRgb(doc: JsPdfDoc, hex: string) {
  const n = hex.replace("#", "");
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  doc.setTextColor(r, g, b);
  doc.setDrawColor(r, g, b);
  doc.setFillColor(r, g, b);
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 2000);
}

function colXs(pageW: number): number[] {
  const inner = pageW - MARGIN_MM * 2;
  const fr = [1.55, 0.72, 0.42, 0.62, 0.55, 0.52, 0.55, 0.72, 0.72];
  const sum = fr.reduce((a, b) => a + b, 0);
  const widths = fr.map((f) => (inner * f) / sum);
  const xs: number[] = [];
  let x = MARGIN_MM;
  for (const w of widths) {
    xs.push(x);
    x += w;
  }
  xs.push(x);
  return xs;
}

export function buildCierrePdfDoc(model: CierreExportModel): JsPdfDoc {
  const page = chooseCierrePdfPage(model.detalle.length);
  const doc = new jsPDF({
    orientation: page.orientation,
    unit: "mm",
    format: page.format,
    compress: false,
  });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const xs = colXs(W);
  const innerW = W - MARGIN_MM * 2;

  let y = MARGIN_MM;
  pdfRgb(doc, "111827");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Cierre diario", MARGIN_MM, y + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  pdfRgb(doc, "374151");
  doc.text(
    `${model.fechaLabel} · ${model.sucursal} · ID ${model.id}`,
    MARGIN_MM,
    y + 13,
  );

  const chipW = 28;
  const chipH = 8;
  const chipX = W - MARGIN_MM - chipW;
  pdfRgb(doc, model.estado === "Abierto" ? "FEF3C7" : "E5E7EB");
  doc.rect(chipX, y + 2, chipW, chipH, "F");
  pdfRgb(doc, "111827");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(model.estado, chipX + chipW / 2, y + 7.2, { align: "center" });

  y += HEADER_MM;

  const cols = 4;
  const gap = 3;
  const cardW = (innerW - gap * (cols - 1)) / cols;
  const cardH = 18;
  model.cards.forEach((card, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = MARGIN_MM + col * (cardW + gap);
    const cy = y + row * (cardH + gap);
    pdfRgb(doc, card.emphasize ? "E5E7EB" : "F3F4F6");
    doc.rect(cx, cy, cardW, cardH, "F");
    pdfRgb(doc, "D1D5DB");
    doc.rect(cx, cy, cardW, cardH, "S");
    pdfRgb(doc, "6B7280");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(card.label, cx + 3, cy + 6);
    pdfRgb(doc, card.emphasize ? "047857" : "111827");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(card.value, cx + 3, cy + 13);
  });
  y += CARDS_MM;

  pdfRgb(doc, "111827");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Detalle de operaciones", MARGIN_MM, y);
  y += 4;

  const headers = CIERRE_EXPORT_PDF_DETALLE_HEADERS;
  const moneyCols = new Set([4, 5, 6]);
  pdfRgb(doc, "E5E7EB");
  doc.rect(MARGIN_MM, y, innerW, TABLE_HEAD_MM, "F");
  pdfRgb(doc, "111827");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  headers.forEach((h, i) => {
    const x = xs[i] + 1.2;
    const align = moneyCols.has(i) ? "right" : "left";
    const tx = align === "right" ? xs[i + 1] - 1.2 : x;
    doc.text(h, tx, y + 5.2, { align });
  });
  y += TABLE_HEAD_MM;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  model.detalle.forEach((row, idx) => {
    const vals = [
      row.concepto,
      row.referencia,
      row.tipo,
      row.metodo,
      row.debe,
      row.usdDh,
      row.haber,
      row.usuario,
      row.destinatario,
    ];
    const wrapped = doc.splitTextToSize(vals[0], xs[1] - xs[0] - 2.4);
    const lines = Array.isArray(wrapped) ? wrapped : [String(wrapped)];
    const rowH = Math.max(ROW_MM, 3.2 + lines.length * 3.2);
    if (idx % 2 === 1) {
      pdfRgb(doc, "F9FAFB");
      doc.rect(MARGIN_MM, y, innerW, rowH, "F");
    }
    pdfRgb(doc, "E5E7EB");
    doc.setLineWidth(0.15);
    doc.line(MARGIN_MM, y + rowH, MARGIN_MM + innerW, y + rowH);
    pdfRgb(doc, "374151");
    vals.forEach((val, i) => {
      const align = moneyCols.has(i) ? "right" : "left";
      const tx = align === "right" ? xs[i + 1] - 1.2 : xs[i] + 1.2;
      if (i === 0) {
        doc.text(lines, tx, y + 4);
      } else {
        const clipped = doc.splitTextToSize(val, xs[i + 1] - xs[i] - 2.4);
        const first = Array.isArray(clipped) ? clipped[0] : String(clipped);
        doc.text(first, tx, y + 4, { align });
      }
    });
    y += rowH;
  });

  y = Math.max(y + 4, H - MARGIN_MM);
  pdfRgb(doc, "6B7280");
  doc.setFontSize(7);
  doc.text(`Página 1 de 1 · ${page.widthMm.toFixed(0)} × ${page.heightMm.toFixed(0)} mm`, MARGIN_MM, H - 6);
  const dolares = model.cards.find((c) => c.label === "Dólares");
  if (dolares) {
    doc.text(`Dólares: ${dolares.value} — sin caption SI USD`, W - MARGIN_MM, H - 6, {
      align: "right",
    });
  }
  return doc;
}

type XlFill = { type: "pattern"; pattern: "solid"; fgColor: { argb: string } };
type XlCell = {
  value?: unknown;
  fill?: XlFill | unknown;
  font?: unknown;
  alignment?: unknown;
  border?: unknown;
  numFmt?: string;
};
type XlRow = {
  height?: number;
  font?: unknown;
  getCell: (n: number) => XlCell;
  eachCell: (opts: { includeEmpty?: boolean }, cb: (cell: XlCell, colNumber: number) => void) => void;
};
type XlCol = {
  width?: number;
  eachCell?: (opts: { includeEmpty?: boolean }, cb: (cell: XlCell) => void) => void;
};
type XlSheet = {
  columns: Array<{ key?: string; width?: number }>;
  views?: unknown;
  mergeCells: (range: string) => void;
  getCell: (r: number, c: number) => XlCell;
  getRow: (n: number) => XlRow;
  getColumn: (n: number) => XlCol;
  addRow: (values: unknown[] | Record<string, unknown>) => XlRow;
};

function xlFill(argb: string): XlFill {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function xlFont(opts: { bold?: boolean; size?: number; color?: string }) {
  return {
    name: "Calibri",
    bold: Boolean(opts.bold),
    size: opts.size ?? 11,
    color: { argb: opts.color ?? XL.text },
  };
}

function paintCellRange(sheet: XlSheet, row: number, lastCol: number, fill: XlFill) {
  for (let c = 1; c <= lastCol; c++) {
    sheet.getCell(row, c).fill = fill;
  }
}

function applyThinBorders(row: XlRow, lastCol: number) {
  for (let c = 1; c <= lastCol; c++) {
    row.getCell(c).border = THIN_BORDER;
  }
}

function moneyFmt(currency: "ARS" | "USD"): string {
  return currency === "USD" ? XL_USD : XL_ARS;
}

function autosizeColumns(sheet: XlSheet, colCount: number, minW: number, maxW: number) {
  for (let i = 1; i <= colCount; i++) {
    const col = sheet.getColumn(i);
    let longest = minW;
    col.eachCell?.({ includeEmpty: false }, (cell) => {
      const raw = cell.value;
      const text = raw == null ? "" : String(raw);
      longest = Math.max(longest, Math.min(text.length + 2, maxW));
    });
    col.width = Math.min(maxW, Math.max(minW, longest));
  }
}

/** True si la fila tiene al menos una celda con fill sólido exceljs. */
export function worksheetRowHasSolidFill(row: XlRow): boolean {
  let found = false;
  row.eachCell({ includeEmpty: true }, (cell) => {
    const fill = cell.fill as { type?: string; fgColor?: { argb?: string } } | undefined;
    if (fill?.type === "pattern" && fill.fgColor?.argb) found = true;
  });
  return found;
}

export async function buildCierreExcelWorkbook(model: CierreExportModel) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "ABS";
  wb.created = new Date();

  const resumen = wb.addWorksheet("Resumen", {
    views: [{ showGridLines: true }],
  }) as XlSheet;
  resumen.columns = [
    { key: "magnitud", width: 22 },
    { key: "importe", width: 18 },
    { key: "criterio", width: 52 },
  ];

  resumen.mergeCells("A1:C1");
  const title = resumen.getCell(1, 1);
  title.value = "Cierre diario";
  title.font = xlFont({ bold: true, size: 16 });
  title.alignment = { vertical: "middle", horizontal: "left" };
  resumen.getRow(1).height = 24;
  paintCellRange(resumen, 1, 3, xlFill(XL.titleBg));

  const meta: Array<[string, string]> = [
    ["Fecha", model.fechaLabel],
    ["Sucursal", model.sucursal],
    ["Estado", model.estado],
  ];
  meta.forEach(([label, value], idx) => {
    const r = idx + 2;
    const row = resumen.getRow(r);
    row.getCell(1).value = label;
    row.getCell(1).font = xlFont({ bold: true, size: 11, color: XL.muted });
    row.getCell(2).value = value;
    row.getCell(2).font = xlFont({ bold: r === 4, size: 11 });
    if (r === 4) {
      row.getCell(2).fill = xlFill(model.estado === "Abierto" ? XL.abierto : XL.cerrado);
      row.getCell(2).alignment = { horizontal: "center" };
    }
  });

  const cardsHeaderRow = 6;
  const cardsHeader = resumen.getRow(cardsHeaderRow);
  cardsHeader.height = 18;
  cardsHeader.getCell(1).value = "Magnitud";
  cardsHeader.getCell(2).value = "Importe";
  cardsHeader.getCell(3).value = "Criterio";
  cardsHeader.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    if (colNumber > 3) return;
    cell.fill = xlFill(XL.thead);
    cell.font = xlFont({ bold: true, size: 11 });
    cell.alignment = {
      vertical: "middle",
      horizontal: colNumber === 2 ? "right" : "left",
    };
    cell.border = THIN_BORDER;
  });

  for (let i = 0; i < model.cards.length; i++) {
    const card = model.cards[i];
    const row = resumen.getRow(cardsHeaderRow + 1 + i);
    row.getCell(1).value = card.label;
    row.getCell(2).value = card.amount;
    row.getCell(3).value = card.nota;
    const fillArgb = CARD_FILLS[card.label] ?? (i % 2 === 1 ? XL.zebra : XL.titleBg);
    applyThinBorders(row, 3);
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber > 3) return;
      cell.fill = xlFill(fillArgb);
      cell.border = THIN_BORDER;
      if (colNumber === 1) {
        cell.font = xlFont({ bold: true, size: 11 });
      } else if (colNumber === 2) {
        cell.numFmt = moneyFmt(card.currency);
        cell.alignment = { horizontal: "right" };
        cell.font = xlFont({
          bold: card.emphasize,
          size: 11,
          color: card.emphasize ? XL.totalAmt : XL.text,
        });
      } else {
        cell.font = xlFont({ size: 10, color: XL.muted });
      }
    });
  }

  await yieldToEventLoop();

  const detalle = wb.addWorksheet("Detalle", {
    views: [{ state: "frozen", ySplit: 1, showGridLines: true }],
  }) as XlSheet;
  detalle.columns = [
    { key: "concepto", width: 42 },
    { key: "referencia", width: 18 },
    { key: "tipo", width: 12 },
    { key: "metodo", width: 16 },
    { key: "importe", width: 16 },
    { key: "usuario", width: 20 },
    { key: "destinatario", width: 22 },
  ];

  const detHead = detalle.addRow([...CIERRE_EXPORT_XLSX_DETALLE_HEADERS]);
  detHead.height = 18;
  detHead.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.fill = xlFill(XL.thead);
    cell.font = xlFont({ bold: true, size: 11 });
    cell.alignment = {
      vertical: "middle",
      horizontal: colNumber === 5 ? "right" : "left",
    };
    cell.border = THIN_BORDER;
  });

  for (let i = 0; i < model.detalle.length; i++) {
    const src = model.detalle[i];
    const importeValue = src.importeNum == null ? "—" : src.importeNum;
    const row = detalle.addRow([
      src.concepto,
      src.referencia,
      src.tipo,
      src.metodo,
      importeValue,
      src.usuario,
      src.destinatario,
    ]);
    applyThinBorders(row, 7);
    if (i % 2 === 1) {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = xlFill(XL.zebra);
        cell.border = THIN_BORDER;
      });
    }
    const importeCell = row.getCell(5);
    if (src.importeNum != null && src.importeCurrency) {
      importeCell.numFmt = moneyFmt(src.importeCurrency);
    }
    importeCell.alignment = { horizontal: "right" };
    if (i > 0 && i % 80 === 0) await yieldToEventLoop();
  }

  autosizeColumns(detalle, 7, 12, 48);
  detalle.getColumn(5).width = Math.max(detalle.getColumn(5).width ?? 14, 14);

  return wb;
}

export async function downloadCierreXlsx(cierre: CierreDiario): Promise<string> {
  await yieldToEventLoop();
  const model = buildCierreExportModel(cierre);
  await yieldToEventLoop();
  const wb = await buildCierreExcelWorkbook(model);
  await yieldToEventLoop();
  const buf = await wb.xlsx.writeBuffer();
  const filename = cierreExportFilename(model, "xlsx");
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerBlobDownload(blob, filename);
  return filename;
}

export async function downloadCierrePdf(cierre: CierreDiario): Promise<string> {
  await yieldToEventLoop();
  const model = buildCierreExportModel(cierre);
  await yieldToEventLoop();
  const doc = buildCierrePdfDoc(model);
  await yieldToEventLoop();
  const filename = cierreExportFilename(model, "pdf");
  const blob = doc.output("blob");
  triggerBlobDownload(blob, filename);
  return filename;
}

/** Wrapper único XLSX/PDF (overlay aparte). Cede el event loop antes de generar. */
export async function downloadCierreExport(
  cierre: CierreDiario,
  kind: "xlsx" | "pdf",
): Promise<string> {
  await yieldToEventLoop();
  return kind === "xlsx" ? downloadCierreXlsx(cierre) : downloadCierrePdf(cierre);
}
